import { QueryClient } from '@tanstack/react-query'
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum'
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage'
import { setDataToLocalStorage } from 'src/localStorage/setDataToLocalStorage'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import {
  TrialStatusData,
  getTrialStatus,
} from 'src/reactQuery/useGetTrialStatusQuery'
import {
  UserFeaturesData,
  getUserFeatures,
} from 'src/reactQuery/useGetUserFeaturesQuery'

// The banner depends on two auth-gated queries, so it can only resolve *after*
// login. Rendering "no banner" first and correcting once the data lands moves
// the whole page (72px padding + the Sidebar accordion) — and users must never
// see that happen. So the state is cached and read synchronously on first
// paint, and prefetched during login for the first-ever visit, which is the
// only moment there is nothing cached to read.
//
// Shared by Layout (reader) and Login (prefetcher) so the two can't drift on
// what "the banner is showing" means.
export interface CachedBannerState {
  customMessage?: {
    text?: string
    title?: string
  }
  isPaymentFailed: boolean
  isTrialExpired: boolean
}

export const NO_BANNER: CachedBannerState = {
  isPaymentFailed: false,
  isTrialExpired: false,
}

export const deriveBannerState = (
  trialData?: TrialStatusData,
  featuresData?: UserFeaturesData
): CachedBannerState => {
  const subscriptionStatus = featuresData?.subscription?.status

  return {
    customMessage: featuresData?.customMessage,
    // Card on file but a payment failed (renewal). Stripe sets past_due/unpaid.
    isPaymentFailed:
      subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid',
    isTrialExpired: !!trialData?.isTrial && !!trialData?.isExpired,
  }
}

export const hasBannerContent = (state: CachedBannerState): boolean => {
  const hasCustomMessage =
    !!state.customMessage?.title?.trim() || !!state.customMessage?.text?.trim()

  return state.isPaymentFailed || state.isTrialExpired || hasCustomMessage
}

export const readCachedBannerState = (): CachedBannerState => {
  const cached = getDataFromLocalStorage<CachedBannerState>(
    LocalStorageKey.BANNER_STATE
  )

  return cached && typeof cached === 'object' ? cached : NO_BANNER
}

export const writeCachedBannerState = (state: CachedBannerState): void => {
  setDataToLocalStorage(LocalStorageKey.BANNER_STATE, state)
}

// Called from the login success handler, before navigating into the app. The
// results are seeded into the query cache too, so Layout's own hooks resolve
// from cache instead of refetching.
//
// Best-effort by design: if either request fails we fall through to the old
// late-resolve behaviour rather than blocking the user out of the app over a
// banner.
export const prefetchBannerState = async (
  queryClient: QueryClient
): Promise<void> => {
  try {
    const [trialResponse, featuresResponse] = await Promise.all([
      queryClient.fetchQuery({
        queryFn: getTrialStatus,
        queryKey: [QueryKey.GET_TRIAL_STATUS],
      }),
      queryClient.fetchQuery({
        queryFn: getUserFeatures,
        queryKey: [QueryKey.GET_USER_FEATURES],
      }),
    ])

    writeCachedBannerState(
      deriveBannerState(trialResponse?.data, featuresResponse?.data)
    )
  } catch {
    // Leave the cache untouched; Layout still resolves it live.
  }
}
